/* eslint-disable react/destructuring-assignment */
import React, { PureComponent } from "react";
import { Route, Routes } from "react-router-dom";
import withRouter from "@tvg/utils/withCustomRouter";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { attempt, get, isEmpty } from "lodash";
import tvgConf from "@tvg/conf";
import LoginService from "@tvg/login-service";

import * as mediatorClassic from "@tvg/mediator-classic/src";
import mediator from "@tvg/mediator";
import calculateFeatureOverride from "@tvg/utils/featuresUtils";
import {
  failFeaturesRequest,
  failMessagesRequest,
  successFeaturesRequest,
  successMessagesRequest
} from "../actions/capi";
import {
  getUserData,
  setUserAccountNumber,
  setUserLoggedState,
  UserData
} from "../actions/user";

import MicroApp from "./components/MicroApp";

type Props = {
  userData: UserData;
  isLogged: boolean;
  dispatch: Dispatch;
  features: [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  featureToggles: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messages: any;
  location: Location;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  history: any;
};

export class Main extends PureComponent<Props> {
  tvg = tvgConf();

  device = get(this.tvg, "device");

  product = get(this.tvg, "product");

  brand = get(this.tvg, "brand");

  componentDidMount() {
    if (typeof window !== "undefined") {
      LoginService.validateSession().then(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (response: { success: { data: any } }) => {
          if (response.success) {
            // @ts-ignore
            this.props.dispatch(getUserData(response.success.data));
            this.props.dispatch(setUserLoggedState(true));
          } else {
            this.props.dispatch(setUserLoggedState(false));
          }
        }
      );
      if (!isEmpty(this.props.features)) {
        const featureToggles = {};
        let featureOverrides = this.getFeaturesOverrides() || {};
        const hasFeaturesOverrides = !!this.getFeaturesOverrides();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.props.features.forEach((toggle: any) => {
          let { enabled } = toggle;
          featureOverrides = calculateFeatureOverride(
            hasFeaturesOverrides,
            featureOverrides,
            toggle
          );
          if (get(featureOverrides, toggle.name)) {
            // eslint-disable-next-line prefer-destructuring
            enabled = featureOverrides[toggle.name].enabled;
          }

          // @ts-ignore
          featureToggles[toggle.name] = enabled;
        });

        if (typeof window !== "undefined" && !isEmpty(featureOverrides)) {
          this.setFeaturesOverrides(featureOverrides);
        }
        this.props.dispatch(successFeaturesRequest(featureToggles));
      } else {
        this.getFeatures();
      }

      this.getMessages();

      // tries to get the user id (accountNumber) from localStorage and dispatch it to user data
      // this is useful because every component initialized by tvg-mobile will know if the user is logged, plus it's
      // account number beforehand
      attempt(() => {
        if (window.sessionStorage.getItem("userId")) {
          this.props.dispatch(
            setUserAccountNumber(
              window.sessionStorage.getItem("userId") as string
            )
          );
        }
      });

      mediatorClassic.subscribe("UPDATE_ROUTER", () => {
        const pathWithParameters = window.location.href.replace(
          window.location.origin,
          ""
        );
        if (this.props.location.pathname !== pathWithParameters) {
          this.props.history.replace(pathWithParameters);
        }
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      mediator.base.subscribe("TVG4_NAVIGATION", (data: any) => {
        mediatorClassic.dispatch("TVG4_NAVIGATION", data.payload);
      });

      mediatorClassic.subscribeWithPast(
        "TVG_LOGIN:USER_SESSION_UPDATE",
        (data: UserData) => {
          this.props.dispatch(getUserData(data));
        }
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      mediator.base.subscribe("TVG_LOGIN:DO_LOGOUT", (data: any) => {
        mediatorClassic.dispatch("TVG_LOGIN:DO_LOGOUT", data.payload);
      });
    }
  }

  componentWillUnmount() {
    // @ts-ignore
    if (this.unlisten) {
      // @ts-ignore
      this.unlisten();
    }
  }

  getFeaturesOverrides = () =>
    attempt(
      () =>
        JSON.parse(window.localStorage.getItem("featureOverrides") as string),
      false
    );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFeaturesOverrides = (features: any) =>
    attempt(() =>
      window.localStorage.setItem("featureOverrides", JSON.stringify(features))
    );

  getFeatures = () =>
    typeof window !== "undefined" &&
    this.tvg
      .getFeatures()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((response: any) => {
        const featureToggles = {};
        let featureOverrides = this.getFeaturesOverrides() || {};
        const hasFeaturesOverrides = !!this.getFeaturesOverrides();

        if (response && Array.isArray(response.featureToggles)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          response.featureToggles.forEach((toggle: any) => {
            let { enabled } = toggle;
            featureOverrides = calculateFeatureOverride(
              hasFeaturesOverrides,
              featureOverrides,
              toggle
            );
            if (get(featureOverrides, toggle.name)) {
              // eslint-disable-next-line prefer-destructuring
              enabled = featureOverrides[toggle.name].enabled;
            }

            // @ts-ignore
            featureToggles[toggle.name] = enabled;
          });
        }

        if (!isEmpty(featureOverrides)) {
          this.setFeaturesOverrides(featureOverrides);
        }

        this.props.dispatch(successFeaturesRequest(featureToggles));
      })
      .catch((err: Error) => this.props.dispatch(failFeaturesRequest(err)));

  getMessages = () => {
    const messageNamespaces = ["Global", "PasswordRecovery"];

    return (
      this.tvg
        .getMessages(messageNamespaces)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) =>
          this.props.dispatch(successMessagesRequest(response))
        )
        .catch((err: Error) => this.props.dispatch(failMessagesRequest(err)))
    );
  };

  render = () => (
    <Routes>
      <Route
        path="/credentials-reset"
        element={
          <MicroApp
            isLogged={this.props.isLogged}
            device={this.device}
            product={this.product}
            brand={this.brand}
            location={this.props.location}
          />
        }
      />
    </Routes>
  );
}

export default connect(
  (store) => ({
    isLogged: get(store, "userData.logged"),
    userData: get(store, "userData"),
    featureToggles: get(store, "capi.featureToggles"),
    features: get(store, "capi.features"),
    // @ts-ignore
    messages: store.capi.messages
  })
  // @ts-ignore
)(withRouter(Main));
